import React from 'react';
import { Box, HStack, Text, IconButton, Tooltip, Badge, Icon } from '@chakra-ui/react';
import { FiCopy, FiCheck } from 'react-icons/fi';
import useAppToast from 'components/toast';

/**
 * Small pieces the setup screens share.
 *
 * ★ EVERY REFUSAL HAS A SENTENCE. The connection layer answers with a code
 * that names exactly what was wrong (a wrong app secret, a number that is not
 * on that account); the screen's job is to say it in words an administrator
 * can act on, next to the field they need to fix.
 */

const FIELD_LABEL = {
	wabaId: 'WhatsApp Business Account ID',
	phoneNumberId: 'Phone number ID',
	appId: 'App ID',
	appSecret: 'App secret',
	accessToken: 'Access token',
};

export const REFUSAL_TEXT = {
	// The workspace's own step, and the only refusal whose fix is another screen.
	WHATSAPP_NOT_CONFIGURED:
		'Your WhatsApp token and API URL are not set yet. Add them in Admin Settings → Configuration under the service "whatsapp" (keys api_url and token), then connect the number.',
	APP_CREDENTIALS_INVALID:
		'Meta rejected the App ID and App secret pair. Check both under App settings → Basic in your Meta app.',
	TOKEN_INVALID: 'Meta says this access token is not valid. Generate a new permanent token for the system user.',
	TOKEN_APP_MISMATCH: 'This access token was issued by a different app than the App ID you entered.',
	WABA_NOT_AUTHORIZED:
		'Your saved token cannot access that WhatsApp Business Account. Check the account ID, and that the system user holding the token has been given this WhatsApp account in Business settings.',
	PHONE_NOT_ON_WABA: 'That phone number ID is not on this WhatsApp Business Account.',
	WABA_ALREADY_CLAIMED: 'This WhatsApp Business Account is already connected to another workspace.',
	TOKEN_INSPECTION_FAILED: 'Meta could not be reached to check the token. Try again in a moment.',
	WABA_UNREACHABLE: 'Meta could not be reached to read the account. Try again in a moment.',
	EXCHANGE_FAILED: 'Meta did not complete the sign-in. Try again.',
	EMBEDDED_SIGNUP_UNAVAILABLE: 'Connecting with Facebook is not available on this installation.',
	CONNECTION_LAYER_UNCONFIGURED: 'The WhatsApp connection service is not configured. Ask your platform operator.',
	CONNECTION_LAYER_UNREACHABLE: 'The WhatsApp connection service is not reachable right now. Try again shortly.',
	TENANT_HAS_NO_DATABASE: 'This workspace is not fully provisioned yet. Ask your platform operator.',
	TENANT_NOT_FOUND: 'This workspace is not known to the connection service. Ask your platform operator.',
};

/** The sentence for a refusal payload from the CRM or the connection layer. */
export function explainRefusal(payload = {}) {
	const code = payload.error ?? payload.reason;
	if (code === 'INVALID_FIELD') {
		return `Check the ${FIELD_LABEL[payload.field] ?? payload.field}: Meta ids are numbers, and tokens and secrets are long strings.`;
	}
	if (code === 'INSUFFICIENT_SCOPE') {
		return `The token is missing permissions: ${(payload.missing ?? []).join(', ')}. Grant them when generating it.`;
	}
	return REFUSAL_TEXT[code] ?? payload.message ?? 'The connection could not be completed.';
}

/** A value to copy into Meta, with the copy button beside it. */
export function CopyField({ label, value, hint, mono = true }) {
	const notify = useAppToast();
	const [copied, setCopied] = React.useState(false);

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			notify.success('Copied', `${label} copied to the clipboard.`);
			setTimeout(() => setCopied(false), 1500);
		} catch (_) {
			notify.error('Not copied', 'Select the value and copy it manually.');
		}
	};

	return (
		<Box>
			<Text fontSize="xs" color="text.muted" mb="1">
				{label}
			</Text>
			{value ? (
				<HStack
					spacing="2"
					px="3"
					py="2"
					bg="bg.input"
					borderWidth="1px"
					borderColor="border.default"
					borderRadius="md"
					align="center"
				>
					<Text
						flex="1"
						fontSize="sm"
						color="text.primary"
						fontFamily={mono ? 'mono' : undefined}
						wordBreak="break-all"
						userSelect="all"
					>
						{value}
					</Text>
					<Tooltip label={copied ? 'Copied' : `Copy ${label.toLowerCase()}`} hasArrow>
						<IconButton
							aria-label={`Copy ${label}`}
							size="sm"
							variant="ghost"
							icon={<Icon as={copied ? FiCheck : FiCopy} color={copied ? 'success' : 'text.secondary'} />}
							onClick={copy}
						/>
					</Tooltip>
				</HStack>
			) : (
				<Text fontSize="sm" color="text.muted" fontStyle="italic">
					{hint}
				</Text>
			)}
		</Box>
	);
}

const TONE = { success: 'green', warning: 'orange', danger: 'red', muted: 'gray', info: 'blue' };

/** A small state label: Connected, Waiting, Not connected. */
export function StatusPill({ tone = 'muted', children }) {
	return (
		<Badge colorScheme={TONE[tone] ?? 'gray'} variant="subtle" borderRadius="full" px="2.5" py="0.5" textTransform="none" fontWeight="medium">
			{children}
		</Badge>
	);
}

/** "in 2 h (13:00)" for expiry lines — the forward-looking twin of `ago`. */
export function until(date) {
	if (!date) return null;
	const s = Math.round((new Date(date).getTime() - Date.now()) / 1000);
	if (s <= 0) return 'now';
	if (s < 3600) return `in ${Math.max(1, Math.round(s / 60))} min`;
	const clock = new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	if (s < 86400) return `in ${Math.round(s / 3600)} h (${clock})`;
	return `on ${new Date(date).toLocaleString()}`;
}

/** "3 minutes ago" for status lines. */
export function ago(date) {
	if (!date) return null;
	const s = Math.max(0, Math.round((Date.now() - new Date(date).getTime()) / 1000));
	if (s < 60) return 'just now';
	if (s < 3600) return `${Math.round(s / 60)} min ago`;
	if (s < 86400) return `${Math.round(s / 3600)} h ago`;
	return new Date(date).toLocaleString();
}
